
import React, { useReducer, useContext, createContext, useMemo, useEffect, useCallback, useRef } from 'react';
import { CartItem, Product } from '../domain/types';
import { useToast } from './ToastContext';
import { useAuth } from './auth';
import { useWallet } from './WalletContext';
import { checkoutService } from '../domain/services/checkoutService';
import { pricingService } from '../domain/services/pricingService';
import { cartService } from '../domain/services/cartService';
import { loggerService } from '../domain/services/loggerService';
import { orderRepository } from '../data/orderRepository';
import { UI_TIMEOUTS } from '../domain/constants/ui';
import { cartReducer, initialCartState, CartState, GuestInfo, ShippingMethodType } from './cartReducer';
import { safeStorage } from '../utils/safeStorage';

export type { ShippingMethodType, GuestInfo };

type CartMeta = Omit<CartState, 'items' | 'isSyncing' | 'discountAmount'>;

interface CartActions {
    addToCart: (product: Product, quantity?: number) => Promise<boolean>;
    removeFromCart: (productId: string) => void;
    decreaseQuantity: (productId: string) => void;
    clearCart: () => void;
    confirmOrder: (paymentMethod: string, walletUsed?: number, existingOrderId?: string) => Promise<string>;
    setShippingCost: (cost: number) => void;
    setShippingMethod: (method: ShippingMethodType) => void;
    setZipCode: (zip: string) => void;
    setGuestInfo: (info: GuestInfo | null) => void;
    applyCoupon: (code: string) => boolean;
    removeCoupon: () => void;
}

const CartStateContext = createContext<(CartState & { total: number; itemCount: number }) | null>(null);
const CartMetaContext = createContext<CartMeta | null>(null);
const CartActionsContext = createContext<CartActions | null>(null);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
const { addToast } = useToast();
const { user } = useAuth();
const { deductWalletBalance, addWalletBalance } = useWallet();

// --- REDUCER STATE ---
const [state, dispatch] = useReducer(cartReducer, {
  ...initialCartState,
  items: safeStorage.local.getObject<CartItem[]>('alltuc_cart') || [],
  guestInfo: safeStorage.local.getObject<GuestInfo>('alltuc_guest')
});

    // --- DERIVED CALCULATIONS ---
    const total = useMemo(() => state.items.reduce((sum, item) => sum + (item.product.price * item.quantity), 0), [state.items]);
    const itemCount = useMemo(() => state.items.reduce((sum, item) => sum + item.quantity, 0), [state.items]);

    // --- AUTO VALIDATION ---
    const validateCart = useCallback(async () => {
        if (state.items.length === 0) return;
        dispatch({ type: 'SET_SYNCING', payload: true });
        const { adjustedItems, changesMade } = await cartService.validateCartItems(state.items);
        if (changesMade) {
            dispatch({ type: 'SET_ITEMS', payload: adjustedItems });
            addToast('Algunos productos de tu carrito se actualizaron por stock.', 'warning');
        }
        dispatch({ type: 'SET_SYNCING', payload: false });
    }, [state.items, addToast]);

    useEffect(() => {
        validateCart();
    }, []); // Only on mount

    // --- CLOUD SYNC (Debounced) ---
    useEffect(() => {
        if (!user) return;

        const sync = async () => {
            dispatch({ type: 'SET_SYNCING', payload: true });
            try {
                await cartService.saveCartToCloud(user.id, state.items);
            } finally {
                dispatch({ type: 'SET_SYNCING', payload: false });
            }
        };

        const timeoutId = setTimeout(sync, UI_TIMEOUTS.CART_SYNC_DEBOUNCE_MS);
        return () => clearTimeout(timeoutId);
    }, [user, state.items]);

// --- CLOUD RECOVERY & MERGE ---
// Track items added during merge to prevent race conditions
const pendingItemsDuringMerge = useRef<CartItem[]>([]);
const mergeInProgress = useRef(false);

useEffect(() => {
  let isMounted = true;
  const recoverAndMerge = async () => {
    if (!user) return;
    
    // Capture initial cart state before async operation
    const initialItems = [...state.items];
    mergeInProgress.current = true;
    pendingItemsDuringMerge.current = [];
    
    dispatch({ type: 'SET_SYNCING', payload: true });
    try {
      const cloudItems = await cartService.getCartFromCloud(user.id);

      if (!isMounted) return;

      if (cloudItems.length > 0) {
        const mergedMap = new Map<string, CartItem>();

        // Add cloud items first
        cloudItems.forEach(item => {
          mergedMap.set(item.product.id, { ...item });
        });

        // Merge initial local items
        initialItems.forEach(localItem => {
          if (mergedMap.has(localItem.product.id)) {
            const existing = mergedMap.get(localItem.product.id)!;
            existing.quantity += localItem.quantity;
          } else {
            mergedMap.set(localItem.product.id, { ...localItem });
          }
        });
        
        // Add any items that were added during the merge
        pendingItemsDuringMerge.current.forEach(item => {
          if (mergedMap.has(item.product.id)) {
            const existing = mergedMap.get(item.product.id)!;
            existing.quantity += item.quantity;
          } else {
            mergedMap.set(item.product.id, { ...item });
          }
        });

        const finalCart = Array.from(mergedMap.values());

        if (isMounted) {
          dispatch({ type: 'SET_ITEMS', payload: finalCart });
          addToast('Tu carrito se ha sincronizado con tu cuenta.', 'info');
          await cartService.saveCartToCloud(user.id, finalCart);
        }
      } else if (initialItems.length > 0) {
        // Save local items to cloud
        await cartService.saveCartToCloud(user.id, initialItems);
      }
    } catch (error) {
      if (isMounted) {
        loggerService.recordError(error instanceof Error ? error : new Error(String(error)), { context: 'cart_merge' });
      }
    } finally {
      mergeInProgress.current = false;
      pendingItemsDuringMerge.current = [];
      if (isMounted) {
        dispatch({ type: 'SET_SYNCING', payload: false });
      }
    }
  };
  recoverAndMerge();
  return () => { isMounted = false; };
}, [user]); // Run ONLY on login (user change)

// --- PERSISTENCE ---
useEffect(() => {
  safeStorage.local.setObject('alltuc_cart', state.items);
}, [state.items]);

useEffect(() => {
  if (state.guestInfo) {
    safeStorage.local.setObject('alltuc_guest', state.guestInfo);
  } else {
    safeStorage.local.remove('alltuc_guest');
  }
}, [state.guestInfo]);

    // Coupon Effect
    useEffect(() => {
        dispatch({ type: 'SET_DISCOUNT_AMOUNT', payload: pricingService.calculateDiscount(state.couponCode, total) });
    }, [total, state.couponCode]);

// --- ACTIONS ---
const addToCart = useCallback(async (product: Product, quantity: number = 1): Promise<boolean> => {
    const existing = state.items.find(item => item.product.id === product.id);
    const currentQty = existing ? existing.quantity : 0;

    const validation = await cartService.validateAddToCart(product, currentQty, quantity);

    if (!validation.success) {
      addToast(validation.message || 'No se puede agregar al carrito', 'error');
      return false;
    }

    // If merge is in progress, queue the item to be added after merge
    if (mergeInProgress.current) {
      const existingPending = pendingItemsDuringMerge.current.find(
        item => item.product.id === product.id
      );
      if (existingPending) {
        existingPending.quantity += quantity;
      } else {
        pendingItemsDuringMerge.current.push({ product, quantity });
      }
      addToast('Producto agregado. Se sincronizará en un momento...', 'info');
    } else {
      dispatch({ type: 'ADD_ITEM', payload: { product, quantity } });
    }

    loggerService.logEvent('add_to_cart', { productId: product.id, name: product.title });
    return true;
  }, [state.items, addToast]);

    const decreaseQuantity = useCallback((productId: string) => {
        dispatch({ type: 'DECREASE_QUANTITY', payload: productId });
    }, []);

    const removeFromCart = useCallback((productId: string) => {
        dispatch({ type: 'REMOVE_ITEM', payload: productId });
        loggerService.logEvent('remove_from_cart', { productId });
    }, []);

const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
    safeStorage.local.remove('alltuc_cart');
    safeStorage.local.remove('alltuc_guest');
    if (user) {
      cartService.saveCartToCloud(user.id, []);
    }
  }, [user]);

    const applyCoupon = useCallback((code: string): boolean => {
        if (pricingService.isValidCoupon(code)) {
            dispatch({ type: 'APPLY_COUPON', payload: code.trim().toUpperCase() });
            loggerService.logEvent('coupon_applied', { code });
            return true;
        }
        return false;
    }, []);

    const removeCoupon = useCallback(() => {
        dispatch({ type: 'REMOVE_COUPON' });
    }, []);

    const confirmOrder = useCallback(async (paymentMethod: string, walletUsed: number = 0, existingOrderId?: string): Promise<string> => {
        try {
            const result = await checkoutService.processOrder({
                items: state.items.map(i => ({ productId: i.product.id, quantity: i.quantity })),
                user,
                guestInfo: state.guestInfo,
                paymentMethod,
                walletUsed,
                shippingMethod: state.shippingMethod,
                zipCode: state.zipCode,
                couponCode: state.couponCode,
                existingOrderId
            });

            if (result.success && result.orderId) {
                const orderId = result.orderId;
                if (!user) {
                    orderRepository.saveGuestOrderId(orderId);
                }
                
                if (paymentMethod !== 'mercadopago') {
                    clearCart();
                }
                
                loggerService.logEvent('purchase', { orderId: orderId, value: total });
                return orderId;
            } else {
                throw new Error(result.error || 'Error desconocido');
            }
        } catch (err) {
            const e = err instanceof Error ? err : new Error(String(err));
            loggerService.recordError(e);
            if (walletUsed > 0 && user) {
                addWalletBalance(walletUsed); 
                addToast('Hubo un error. Se ha reintegrado tu saldo.', 'warning');
            } else {
                addToast(e.message || 'Error al procesar la orden', 'error');
            }
            throw e;
        }
    }, [state.items, state.guestInfo, state.shippingMethod, state.zipCode, state.couponCode, user, clearCart, addToast, total]);

    const setShippingCost = useCallback((cost: number) => {
        dispatch({ type: 'SET_SHIPPING_COST', payload: cost });
    }, []);

    const setShippingMethod = useCallback((method: ShippingMethodType) => {
        dispatch({ type: 'SET_SHIPPING_METHOD', payload: method });
    }, []);

    const setZipCode = useCallback((zip: string) => {
        dispatch({ type: 'SET_ZIP_CODE', payload: zip });
    }, []);

    const setGuestInfo = useCallback((info: GuestInfo | null) => {
        dispatch({ type: 'SET_GUEST_INFO', payload: info });
    }, []);

    const stateValue = useMemo(() => ({ ...state, total, itemCount }), [state, total, itemCount]);
    const metaValue = useMemo(() => ({
        shippingCost: state.shippingCost,
        shippingMethod: state.shippingMethod,
        zipCode: state.zipCode,
        guestInfo: state.guestInfo,
        couponCode: state.couponCode
    }), [state.shippingCost, state.shippingMethod, state.zipCode, state.guestInfo, state.couponCode]);
    const actionsValue = useMemo(() => ({
        addToCart, removeFromCart, decreaseQuantity, clearCart, confirmOrder,
        setShippingCost, setShippingMethod, setZipCode, setGuestInfo, applyCoupon, removeCoupon
    }), [addToCart, removeFromCart, decreaseQuantity, clearCart, confirmOrder, applyCoupon, removeCoupon]);

    return (
        <CartActionsContext.Provider value={actionsValue}>
            <CartMetaContext.Provider value={metaValue}>
                <CartStateContext.Provider value={stateValue}>
                    {children}
                </CartStateContext.Provider>
            </CartMetaContext.Provider>
        </CartActionsContext.Provider>
    );
};

export const useCartState = () => {
    const context = useContext(CartStateContext);
    if (!context) throw new Error("useCartState must be used within CartProvider");
    return context;
};

export const useCartMeta = () => {
    const context = useContext(CartMetaContext);
    if (!context) throw new Error("useCartMeta must be used within CartProvider");
    return context;
};

export const useCartActions = () => {
    const context = useContext(CartActionsContext);
    if (!context) throw new Error("useCartActions must be used within CartProvider");
    return context;
};

export const useCart = () => {
    const state = useCartState();
    const meta = useCartMeta();
    const actions = useCartActions();
    return { ...state, ...meta, ...actions };
};
